// src/services/pftSoftChecks.js
//
// Soft checks (decided 2026-10-20): an address, height or weight that only ONE of the two
// extraction readings found (or two nearly identical addresses, the longer kept) is kept
// without holding the card back on PFT Uploads. The extraction job stores it on the PFT
// date (`test.softChecks[key] = { reason, readings }`, pft_quick_test/cloud_job/
// portal_import.py), and it travels with the date when the patient is moved or merged.
// On the patient page staff can open the PDF and tick "Verified"; the tick is stored on
// the same entry (`verifiedBy`, `verifiedAt`) and audited (field names only).
//
// 2026-10-27: ANY PFT value only one reading found is kept with a soft check (so filters
// see it), and so are address differences and a DOB found only by the second look. Their
// labels come from PFT_TEST_FIELDS. `oneReading` marks the "only one reading found it"
// kind, which PFT Uploads outlines and the filtered export flags.
//
// Pure, so pftSoftChecks.test.js covers it.

import { PFT_TEST_FIELDS } from "./pftTests";

export const SOFT_CHECK_LABELS = { address: "Address", height: "Height", weight: "Weight" };
const FIXED_LABELS = { ...SOFT_CHECK_LABELS, dob: "Date of birth" };

/** What a soft-checked value is called on the page. (PFT_TEST_FIELDS is read when this
 *  runs, not when the file loads: pftTests.js imports this file too.) */
export const softCheckLabel = (key) =>
  FIXED_LABELS[key] || ((PFT_TEST_FIELDS || []).find((f) => f.key === key) || {}).label || key;

// Address, height, weight, DOB first; then the PFT values in the page's own order.
const order = (key) => {
  const fixed = Object.keys(FIXED_LABELS).indexOf(key);
  if (fixed >= 0) return fixed;
  const i = (PFT_TEST_FIELDS || []).findIndex((f) => f.key === key);
  return 100 + (i < 0 ? 999 : i);
};

// The extraction job's reason when only ONE of the two readings found a value.
export const ONE_READING = "only one reading found it";
const UNITS = { height: "heightUnit", weight: "weightUnit" };

// The job's reasons (portal_import.SOFT_ONE_READING / SOFT_SIMILAR), in plain words.
const REASONS = {
  "only one reading found it": "Only one of the two readings found it",
  "the two readings were nearly the same; the longer one was kept":
    "The two readings were nearly the same; the longer one was kept",
  // 2026-10-27
  "the two readings differ only in the ZIP code; the one with the whole ZIP was kept":
    "The two readings differ only in the ZIP code; the one with the whole ZIP was kept",
  "the two readings differ; the first reading was kept": "The two readings differ; the first reading was kept",
  "no reading found it; a second look at the PDF did (check it against the PDF)":
    "Neither reading found it; a second look at the PDF did",
};

const str = (v) => (v === undefined || v === null ? "" : String(v).trim());
const norm = (v) => str(v).replace(/\s+/g, " ").toLowerCase();

/**
 * The soft checks still worth showing on a PFT date: only while the value is still the
 * one the PDF gave (a value staff changed, or an address kept from RegX, isn't the PDF's).
 *   patientAddress / patientDob: the patient's address / DOB now (they live on the patient).
 * -> [{ key, label, value, reason, oneReading, verified, verifiedBy, verifiedAt }]
 */
export const softChecksOf = (test, { patientAddress = "", patientDob = "" } = {}) => {
  const checks = (test && test.softChecks) || {};
  return Object.keys(checks)
    .filter((key) => checks[key] && typeof checks[key] === "object")
    .sort((a, b) => order(a) - order(b))
    .map((key) => {
      const c = checks[key];
      const current = key === "address" ? patientAddress : key === "dob" ? patientDob : test[key];
      if (!str(current) || !(c.readings || []).some((r) => str(r) && norm(r) === norm(current))) return null;
      const unit = UNITS[key] ? str(test[UNITS[key]]) : "";
      return {
        key,
        label: softCheckLabel(key),
        value: unit ? `${str(current)} ${unit}` : str(current),
        reason: REASONS[c.reason] || "Found by one reading only",
        oneReading: c.reason === ONE_READING,
        verified: !!c.verifiedAt,
        verifiedBy: c.verifiedBy || "",
        verifiedAt: c.verifiedAt || "",
        // Settled by the person's choice in a merge (Keep RegX / Use PFT), not a tick.
        decidedInMerge: c.verifiedIn === "merge",
      };
    })
    .filter(Boolean);
};

/** How many soft checks across these tests still wait for someone to verify them. */
export const unverifiedSoftChecks = (tests = [], opts = {}) =>
  tests.filter((t) => t && !t.removedAt).reduce((n, t) => n + softChecksOf(t, opts).filter((c) => !c.verified).length, 0);

/** The test after ticking (or unticking) "Verified" on one soft check. */
export const withVerification = (test, key, { checked, by = "", now }) => {
  const checks = { ...((test && test.softChecks) || {}) };
  const { verifiedBy, verifiedAt, verifiedIn, ...rest } = checks[key] || {};
  checks[key] = checked ? { ...rest, verifiedBy: by, verifiedAt: now } : rest;
  return { ...test, softChecks: checks };
};

/**
 * Merge soft checks for a save (pftTests.mergeTests): per key, a check this page changed
 * (ticked or unticked since loading) wins; otherwise the stored one, so a tick made in
 * another tab meanwhile survives.
 */
export const mergeSoftChecks = (base = {}, fresh = {}, local = {}) => {
  const out = { ...(fresh || {}) };
  for (const key of new Set([...Object.keys(local || {}), ...Object.keys(base || {})])) {
    if (JSON.stringify((local || {})[key] ?? null) !== JSON.stringify((base || {})[key] ?? null)) {
      if ((local || {})[key] === undefined) delete out[key];
      else out[key] = local[key];
    }
  }
  return out;
};

/**
 * What a save verified or un-verified, for the audit trail: field names and dates only,
 * never the value. -> [{ testId, date, key, label, verified }]
 */
export const verificationChanges = (base = [], tests = []) => {
  const before = new Map((base || []).filter((t) => t && t.id).map((t) => [t.id, t]));
  const out = [];
  for (const t of tests || []) {
    if (!t || !t.id) continue;
    const was = ((before.get(t.id) || {}).softChecks) || {};
    const now = t.softChecks || {};
    for (const key of [...new Set([...Object.keys(was), ...Object.keys(now)])].sort((x, y) => order(x) - order(y))) {
      const a = !!(was[key] && was[key].verifiedAt);
      const b = !!(now[key] && now[key].verifiedAt);
      if (a !== b) out.push({ testId: t.id, date: str(t.date), key, label: softCheckLabel(key), verified: b });
    }
  }
  return out;
};

/** A test without one soft check (e.g. the address, when the merge kept RegX's). */
export const withoutSoftCheck = (test, key) => {
  if (!test || !test.softChecks || !test.softChecks[key]) return test;
  const { [key]: _drop, ...rest } = test.softChecks;
  const out = { ...test };
  if (Object.keys(rest).length) out.softChecks = rest;
  else delete out.softChecks;
  return out;
};
